import moment from 'moment';
import { createReducer } from 'typesafe-actions';
import { EBookType, IBook, IInspectorStore } from './types';
import inspectorActions, {
    EInspectorConstants,
    TInspectorActions,
} from './actions';
import { marpolBooks, sealLogsBooks } from './constants';
import { searchFilter } from './utils';
import withJobs from 'redux/_core/job/reducers';

const defaultState: IInspectorStore = {
    _jobs: {},
    filters: {
        dateRange: {
            endDate: null,
            startDate: null,
        },
        entry: {
            id: '',
            title: '',
            typeOfLogs: '',
            type: '',
        },
    },
    pagination: {
        count: 0,
        index: 0,
    },
    logs: [],
    sealLogs: [],
    selectedLog: {
        log: [],
    },
    indexOfTasks: {
        marpol: marpolBooks,
    },
    books: [],
    filteredBy: null,
    searchBy: '',
    showCoverPages: false,
    stateOfScriptInjection: 'IDLE',
};

const inspectorReducers = createReducer<IInspectorStore, TInspectorActions>(
    defaultState
)
    .handleAction(
        inspectorActions.document.setDateRange,
        function setDateRange(state, action) {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    dateRange: action.payload.dateRange,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.commands.clearDateRange,
        function clearDateRange(state, action) {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    dateRange: { startDate: null, endDate: null },
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setEntry,
        function setEntry(state, action) {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    entry: action.payload.entry,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogsCount,
        function setLogsCount(state, action) {
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    count: action.payload.count,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setIndex,
        function setIndex(state, action) {
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    index: action.payload.index,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogs,
        function setLogs(state, action) {
            return {
                ...state,
                logs: action.payload.logs,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setSealLogs,
        function setSealLogs(state, action) {
            return {
                ...state,
                sealLogs: action.payload.sealLogs,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setSelectedLog,
        function setSelectedLog(state, action) {
            return {
                ...state,
                selectedLog: action.payload.selectedLog,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogId,
        function setLogId(state, action) {
            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    selectedLogId: action.payload.selectedLogId,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogType,
        function setLogType(state, action) {
            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    logType: action.payload.logType,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogConfig,
        function setLogConfig(state, action) {
            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    logConfig: action.payload.logConfig,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setLogPrintTemplate,
        function setLogPrintTemplate(state, action) {
            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    logPrintTemplate: action.payload.logPrintTemplate,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.logSelect,
        function logSelect(state, action) {
            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    log: action.payload.log,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setAssociatedDayLog,
        function setAssociatedDayLog(state, action) {
            const tempLog = [...state.selectedLog.log];
            // check whether the log is an array and the first index exists
            if (tempLog && Array.isArray(tempLog) && tempLog.length > 0) {
                // we need to set the associated day log
                tempLog[0].associatedDayLog = action.payload.associatedDayLog;
            }

            return {
                ...state,
                selectedLog: {
                    ...state.selectedLog,
                    log: tempLog,
                },
            };
        }
    )
    .handleAction(
        inspectorActions.document.setIndexOfTasks,
        function setIndexOfTasks(state, action) {
            const { indexOfTasks } = action.payload;
            const books: IBook[] = [];
            if (indexOfTasks) {
                if (indexOfTasks?.logs && Array.isArray(indexOfTasks?.logs)) {
                    indexOfTasks.logs.forEach((log: any) => {
                        books.push({
                            ...log,
                            bookType: EBookType.LOG,
                        });
                    });
                }
                if (
                    indexOfTasks?.checklists &&
                    Array.isArray(indexOfTasks?.checklists)
                ) {
                    indexOfTasks.checklists.forEach((checklist: any) => {
                        books.push({
                            ...checklist,
                            bookType: EBookType.CHECKLIST,
                        });
                    });
                }
                if (
                    indexOfTasks?.permits &&
                    Array.isArray(indexOfTasks?.permits)
                ) {
                    indexOfTasks.permits.forEach((permit: any) => {
                        books.push({
                            ...permit,
                            bookType: EBookType.PERMIT,
                        });
                    });
                }

                // set marpol books
                books.push(...marpolBooks);
            }
            books.push(...sealLogsBooks);
            return {
                ...state,
                books,
                indexOfTasks,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setSearchBy,
        function setSearchBy(state, action) {
            const { searchBy } = action.payload;
            return {
                ...state,
                searchBy,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setBooksUsingSearchBy,
        function setBooksUsingSearchBy(state, action) {
            const { searchBy } = action.payload;
            let books: IBook[] = [];
            const { indexOfTasks, filteredBy } = state;
            if (indexOfTasks) {
                let logs: any = [];
                let checklists: any = [];
                let permits: any = [];
                const newBooks: any = [];
                if (indexOfTasks?.logs && Array.isArray(indexOfTasks?.logs)) {
                    logs = indexOfTasks.logs.map((log: any) => {
                        newBooks.push({
                            ...log,
                            bookType: EBookType.LOG,
                        });
                        return {
                            ...log,
                            bookType: EBookType.LOG,
                        };
                    });
                    logs = [...logs, ...sealLogsBooks];
                    newBooks.push(...sealLogsBooks);
                }
                if (
                    indexOfTasks?.checklists &&
                    Array.isArray(indexOfTasks?.checklists)
                ) {
                    checklists = indexOfTasks.checklists.map(
                        (checklist: any) => {
                            newBooks.push({
                                ...checklist,
                                bookType: EBookType.CHECKLIST,
                            });
                            return {
                                ...checklist,
                                bookType: EBookType.CHECKLIST,
                            };
                        }
                    );
                }
                if (
                    indexOfTasks?.permits &&
                    Array.isArray(indexOfTasks?.permits)
                ) {
                    permits = indexOfTasks.permits.map((permit: any) => {
                        newBooks.push({
                            ...permit,
                            bookType: EBookType.PERMIT,
                        });
                        return {
                            ...permit,
                            bookType: EBookType.PERMIT,
                        };
                    });
                }

                newBooks.push(...marpolBooks);

                if (filteredBy === EBookType.LOG) {
                    books =
                        searchBy && searchBy.trim()
                            ? (searchFilter(
                                  logs,
                                  searchBy
                              ) as any)
                            : logs;
                } else if (filteredBy === EBookType.CHECKLIST) {
                    books =
                        searchBy && searchBy.trim()
                            ? (searchFilter(
                                  checklists,
                                  searchBy
                              ) as any)
                            : checklists;
                } else if (filteredBy === EBookType.PERMIT) {
                    books =
                        searchBy && searchBy.trim()
                            ? (searchFilter(
                                  permits,
                                  searchBy
                              ) as any)
                            : permits;
                } else if (filteredBy === EBookType.MARPOL) {
                    books =
                        searchBy && searchBy.trim()
                            ? (searchFilter(
                                  marpolBooks as any,
                                  searchBy
                              ) as any)
                            : marpolBooks;
                } else {
                    books =
                        searchBy && searchBy.trim()
                            ? (searchFilter(
                                  newBooks,
                                  searchBy
                              ) as any)
                            : newBooks;
                }
            }
            return {
                ...state,
                books,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setFilteredBy,
        function setFilteredBy(state, action) {
            const { filteredBy } = action.payload;
            const books: IBook[] = [];
            const { indexOfTasks, searchBy } = state;
            if (indexOfTasks) {
                if (
                    filteredBy === EBookType.LOG &&
                    indexOfTasks?.logs &&
                    Array.isArray(indexOfTasks?.logs)
                ) {
                    indexOfTasks.logs.forEach((log: any) => {
                        books.push({
                            ...log,
                            bookType: EBookType.LOG,
                        });
                    });
                    books.push(...sealLogsBooks);
                }
                if (
                    filteredBy === EBookType.CHECKLIST &&
                    indexOfTasks?.checklists &&
                    Array.isArray(indexOfTasks?.checklists)
                ) {
                    indexOfTasks.checklists.forEach((checklist: any) => {
                        books.push({
                            ...checklist,
                            bookType: EBookType.CHECKLIST,
                        });
                    });
                }
                if (
                    filteredBy === EBookType.PERMIT &&
                    indexOfTasks?.permits &&
                    Array.isArray(indexOfTasks?.permits)
                ) {
                    indexOfTasks.permits.forEach((permit: any) => {
                        books.push({
                            ...permit,
                            bookType: EBookType.PERMIT,
                        });
                    });
                }
                if (
                    filteredBy === EBookType.MARPOL &&
                    Array.isArray(marpolBooks)
                ) {
                    books.push(...marpolBooks);
                }
            }
            return {
                ...state,
                books:
                    searchBy && searchBy?.trim()
                        ? (searchFilter(
                              books as any,
                              searchBy
                          ) as any)
                        : books,
                filteredBy,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setShowCoverPages,
        function setShowCoverPages(state, action) {
            return {
                ...state,
                showCoverPages: action.payload.showCoverPage,
            };
        }
    )
    .handleAction(
        inspectorActions.document.setStateOfScriptInjection,
        function setStateOfScriptInjection(state, action) {
            const { stateOfScriptInjection } = action.payload;
            return {
                ...state,
                stateOfScriptInjection,
            };
        }
    );

export default withJobs<IInspectorStore>(
    EInspectorConstants.FEATURE,
    defaultState,
    inspectorReducers
);
